<script setup lang="ts">
/* eslint-disable vue/no-mutating-props */
import RichTextEditor from "@/components/text-editor/index.vue";
import { useEmitter } from "@/composition/useEmitter";
import { useIMEComposition } from "@/composition/useIMEComposition";
import { MediaCenterSubCategory } from "@/helpers/mediaCenter.type";
import { FormServices } from "@/service/FormServices";
import { SurveyServices } from "@/service/SurveyServices";
import { useAppStore } from "@/store/app";
import {
  cloneElementWithUniqueHiddenKey,
  fixSafariCursor,
  focusLabelAfterActiveChange,
  getCaretOffsetWithin,
  handleLabelTabNavigation,
  handleShortLabelInput as handleShortLabelInputUtil,
  isBuilderElementActive,
  scrollContainerBySlideHeight,
  setCaretOffsetWithin,
  showFieldLabel,
} from "@/util/methods";
import {
  Copy05Icon,
  Mail01Icon,
  PlusIcon,
  Settings02Icon,
  Trash01Icon,
} from "@gohighlevel/ghl-icons/24/outline";
import { UIButton } from "@gohighlevel/ghl-ui";
import uuid4 from "uuid4";
import { computed, nextTick, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import {
  CaptchaIcon,
  CONSTANTS,
  JUMP_TO_FIELDS,
  TERMS_AND_CONDITIONS_LABEL,
} from "../../util/constants";
import AddressCardPreview from "./Address/AddressCardPreview2.vue";
import AlertModal from "./AlertModal.vue";
import TextElement from "./Fields/TextElement.vue";
import InlineRTE from "./InlineRTE.vue";
import Payment from "./Payments/Payment.vue";
import SelectField from "./SelectField.vue";
import StandardImageUpload from "./StandardImageUpload.vue";

const { t } = useI18n();
const store = useAppStore();
const emitter = useEmitter();
const { handleCompositionStart, handleCompositionEnd, withIMECheck, resetKey } =
  useIMEComposition();
const props = defineProps({
  element: {
    type: Object,
    default() {
      return {};
    },
  },
  fieldStyle: {
    type: Object,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  slideIndex: {
    type: Number,
    default: undefined,
  },
  currentElement: {
    type: Object,
    required: true,
  },
  formLabelVisible: {
    type: Boolean,
    default: true,
  },
  slide: {
    type: Object,
    default: () => {
      return {};
    },
  },
  mediaCenterSubCategory: {
    type: String as () => MediaCenterSubCategory,
    required: true,
  },
});

const emit = defineEmits([
  "update:label",
  "update:placeholder",
  "update:shortLabel",
]);
const editableLabel = ref<HTMLElement | null>(null);

// Device-aware active label
const activeLabel = computed<string>(() => {
  return store.deviceMode === "mobile"
    ? String((props.element as any)?.mobileLabel ?? props.element?.label ?? "")
    : String(props.element?.label ?? "");
});

const localLabel = ref(activeLabel.value || "");

// Handler method to call the imported fixSafariCursor function
const handleSafariCursor = (e: Event) => {
  const isKeyDown = e.type === "keydown";
  fixSafariCursor(e, isKeyDown);
};

watch(
  [
    () => props.element.label,
    () => (props.element as any).mobileLabel,
    () => store.deviceMode,
  ],
  () => {
    localLabel.value = activeLabel.value || "";
  }
);

const showDeleteModal = ref(false);
const localPlaceholder = ref(props.element.placeholder || "");
const localShortLabel = ref(props.element.shortLabel || "");

watch(
  () => props.element.placeholder,
  (newVal) => {
    localPlaceholder.value = newVal || "";
  }
);

watch(
  () => props.element.shortLabel,
  (newVal) => {
    localShortLabel.value = newVal || "";
  }
);

const updateLocalShortLabel = () => {
  onUpdateShortLabel(localShortLabel.value);
};

const updateLocalPlaceholder = () => {
  onUpdatePlaceholder(localPlaceholder.value);
};

const inputClicked = ref(false);

const onLabelBlur = () => {
  store.addToHistory();
  inputClicked.value = false;
};

const onLabelInput = () => {
  if (store.deviceMode === "mobile") {
    (props.element as any).mobileLabel = localLabel.value || "";
    store.anyUnsavedChanges = true;
  } else {
    emit("update:label", localLabel.value);
  }
};

const onUpdatePlaceholder = (newPlaceholder: string) => {
  emit("update:placeholder", newPlaceholder);
  store.addToHistory();
};

const onUpdateShortLabel = (newShortLabel: string) => {
  emit("update:shortLabel", newShortLabel);
  store.addToHistory();
};

const handleFocus = (event: FocusEvent) => {
  const target = event.target as HTMLElement;
  const range = document.createRange();
  range.selectNodeContents(target);
  const selection = window.getSelection();
  if (selection) {
    selection.removeAllRanges();
    selection.addRange(range);
  }
};

// moved to util/methods.ts (getUniqueCopyHiddenKey)

const deleteIndex = ref<number | null>(null);

const onFileChange = (e, index, slideIndex) => {
  const element = e.target;
  if (!element.files) {
    return;
  }
  for (let i = 0; i < element.files.length; i++) {
    imageUploadAndReszie(element.files[i], index, slideIndex);
  }
  element.files = null;
};

const imageUploadAndReszie = async (file: File, index, slideIndex) => {
  const bodyFormData = new FormData();
  let image;
  bodyFormData.append("locationId", store.locationId);
  bodyFormData.append("file", file);
  bodyFormData.append("type", "builder");
  if (store.formId) {
    bodyFormData.append("formId", store.formId);
    image = await FormServices.imageUpload(bodyFormData);
  } else {
    bodyFormData.append("surveyId", store.surveyId);
    image = await SurveyServices.imageUpload(bodyFormData);
  }

  setTimeout(() => {
    if (store.formId) {
      store.elements[
        index
      ].url = `https://images.leadconnectorhq.com/image/f_webp/q_85/r_1000/u_${image.data.imageUrl}`;
    } else {
      store.slides[slideIndex].slideData[
        index
      ].url = `https://images.leadconnectorhq.com/image/f_webp/q_85/r_1000/u_${image.data.imageUrl}`;
    }
  }, 1000);
};

const successCallbackDeleteUpdate = () => {
  store.builderConfig.conditionalLogic =
    store.builderConfig.conditionalLogic.filter((item) => {
      item.conditions = item?.conditions?.filter(
        (condition) =>
          condition.selectedField !==
          store.elements[deleteIndex.value].hiddenFieldQueryKey
      );
      return item.conditions?.length > 0;
    });
  store.elements.splice(deleteIndex.value, 1);
  showDeleteModal.value = false;
};

const cancelCallbackDeleteUpdate = () => {
  showDeleteModal.value = false;
};

const removeField = (tag: string, index: number, slideIndex?: number) => {
  store.anyUnsavedChanges = true;
  // For Survey or Quiz
  if (store.surveyId || store.quizId) {
    // Clear any references to the element being removed
    if (slideIndex !== undefined) {
      store.slides[slideIndex].slideData.splice(index, 1);
      // Reset active element state
      store.isAnyElementActive = false;
      store.currentActiveIndex = [undefined, undefined];
    }
  } else {
    // For Forms
    // Check if this field is used in conditional logic
    if (
      store.builderConfig.conditionalLogic?.some((item) =>
        item.conditions?.some(
          (condition) =>
            condition.selectedField ===
            store.elements[index].hiddenFieldQueryKey
        )
      )
    ) {
      // Show confirmation modal before deleting
      showDeleteModal.value = true;
      deleteIndex.value = index;
    } else {
      // Safe to delete immediately
      store.elements.splice(index, 1);
      // Reset active element reference if it was the deleted element
      if (store.currentCustomSidebarPreview === index) {
        store.currentCustomSidebarPreview = undefined;
      } else if (
        typeof store.currentCustomSidebarPreview === "number" &&
        store.currentCustomSidebarPreview > index
      ) {
        // Adjust the index for elements that come after the deleted one
        store.currentCustomSidebarPreview--;
      }
    }
    // Special handling for payment elements
    if (tag === "payment") {
      store.addedProductList = [];
      store.formAction.actionType = "2";
      store.payment = null;
      // add condition to remove the recurring product id while loaded from snapshot
      if (
        (store.builder?.originId || store.builder?.source === "share") &&
        store.builder?.formData?.recurringProductId
      ) {
        store.builderConfig.recurringProductId = null;
        store.builderConfig.recurringProductCurrency = null;
        store.builderConfig.recurringProducts = [];
      }
    }
  }
  // Record this change in history
  store.addToHistory();
};

// Open settings for the active element (left/right sidebar depending on context)
const openSettings = (index: number, slideIndex?: number) => {
  // Forms
  if (store.formId) {
    store.currentCustomSidebarPreview = index;
    store.isAnyElementActive = true;
    return;
  }
  // Quizzes
  if (store.quizId && props.slide && props.element?.uuid) {
    store.currentActiveIndex = [props.slide.id, props.element.uuid];
    store.currentSlideIndex = slideIndex;
    store.sidebarOverlaySettings = {
      elementId: props.element.uuid,
      slideId: props.slide.id,
      formIndex: undefined,
    };
    store.isAnyElementActive = true;
    return;
  }
  // Surveys
  if (store.surveyId) {
    store.currentActiveIndex = [slideIndex, index];
    store.currentSlideIndex = slideIndex;
    store.sidebarOverlaySettings = { index, slideIndex, formIndex: undefined };
    store.isAnyElementActive = true;
  }
};

// Clone the current element next to its position (restricted to allowed tags)
const cloneElement = (index: number, slideIndex?: number) => {
  const tag = props.element?.tag;
  if (!tag) {
    return;
  }
  if (!store.duplicateAllowed?.includes(tag)) {
    return;
  }

  // Forms
  if (store.formId) {
    const existingKeys = store.elements
      .map((e: any) => e?.hiddenFieldQueryKey)
      .filter((k: any) => typeof k === "string") as string[];
    const cloned = cloneElementWithUniqueHiddenKey(
      store.elements[index],
      existingKeys
    );
    // Insert right after current
    store.elements.splice(index + 1, 0, cloned);
    store.anyUnsavedChanges = true;
    store.addToHistory();
    // Activate the newly cloned field
    store.currentCustomSidebarPreview = index + 1;
    store.isAnyElementActive = true;
    return;
  }

  // Surveys / Quizzes
  if ((store.surveyId || store.quizId) && typeof slideIndex === "number") {
    const list = store.slides[slideIndex].slideData;
    const existingKeys = list
      .map((e: any) => e?.hiddenFieldQueryKey)
      .filter((k: any) => typeof k === "string") as string[];
    const cloned = cloneElementWithUniqueHiddenKey(list[index], existingKeys, {
      ensureUuid: true,
    });
    // Quiz: place quiz questions on a new page, other elements on the same page
    if (store.quizId) {
      const isQuestion = JUMP_TO_FIELDS.includes(
        (props.element as any)?.dataType
      );
      const hasQuestionOnSlide = list.some((el: any) =>
        JUMP_TO_FIELDS.includes(el?.dataType)
      );
      if (isQuestion && hasQuestionOnSlide) {
        const newSlideIndex = slideIndex + 1;
        const newSlide = {
          slideName: t("quiz.pageLabel", { n: store.slides.length + 1 }),
          slideData: [cloned],
          id: uuid4(),
        } as any;
        store.slides.splice(newSlideIndex, 0, newSlide);
        store.formAction.fieldPerPage = store.slides.length;
        store.anyUnsavedChanges = true;
        store.addToHistory();
        // Scroll by current slide's height to bring the new slide into view
        scrollContainerBySlideHeight(
          "form-builder",
          "form-builder-container",
          ".survey-form-container",
          slideIndex,
          20
        );
        // Activate only the cloned question on the new page
        store.currentActiveIndex = [newSlide.id, cloned.uuid];
        store.currentSlideIndex = newSlide.id;
        store.sidebarOverlaySettings = {
          elementId: cloned.uuid,
          slideId: newSlide.id,
          formIndex: undefined,
        } as any;
        store.isAnyElementActive = true;
        return;
      }
      // Otherwise, insert next to the original in the same page
      list.splice(index + 1, 0, cloned);
      store.anyUnsavedChanges = true;
      store.addToHistory();
      if (props.slide && cloned?.uuid) {
        store.currentActiveIndex = [props.slide.id, cloned.uuid];
        store.currentSlideIndex = props.slide.id as any;
        store.sidebarOverlaySettings = {
          elementId: cloned.uuid,
          slideId: props.slide.id as any,
          formIndex: undefined,
        } as any;
        store.isAnyElementActive = true;
      }
      return;
    }
    // In one-question-per-page mode for surveys, move the clone to a new slide
    if (store.surveyId && store.formAction.fieldSettingEnable) {
      const newSlideIndex = slideIndex + 1;
      const newSlide = {
        slideName: t("survey.slideLabel", { n: store.slides.length + 1 }),
        slideData: [cloned],
        id: new Date().getTime() + "-" + store.slides.length,
      } as any;
      store.slides.splice(newSlideIndex, 0, newSlide);
      store.formAction.fieldPerPage = store.slides.length;
      store.anyUnsavedChanges = true;
      store.addToHistory();
      // Activate the cloned field on the new slide
      store.currentActiveIndex = [newSlideIndex, 0];
      store.currentSlideIndex = newSlideIndex;
      store.sidebarOverlaySettings = {
        index: 0,
        slideIndex: newSlideIndex,
        formIndex: undefined,
      } as any;
      store.isAnyElementActive = true;
    } else {
      // Default behavior: insert clone next to the original in the same slide
      list.splice(index + 1, 0, cloned);
      store.anyUnsavedChanges = true;
      store.addToHistory();
      // Activate the newly cloned field for surveys/quizzes
      if (store.quizId && props.slide && cloned?.uuid) {
        store.currentActiveIndex = [props.slide.id, cloned.uuid];
        store.currentSlideIndex = slideIndex;
        store.sidebarOverlaySettings = {
          elementId: cloned.uuid,
          slideId: props.slide.id,
          formIndex: undefined,
        } as any;
        store.isAnyElementActive = true;
      } else if (store.surveyId) {
        store.currentActiveIndex = [slideIndex, index + 1];
        store.currentSlideIndex = slideIndex;
        store.sidebarOverlaySettings = {
          index: index + 1,
          slideIndex,
          formIndex: undefined,
        } as any;
        store.isAnyElementActive = true;
      }
    }
  }
};

// Delete handler aligned to reference naming
const deleteActiveElement = (index: number, slideIndex?: number) => {
  removeField(props.element?.tag, index, slideIndex);
};

const isActiveElement = (slideIndex, elementIndex, elementId, slideId) => {
  return isBuilderElementActive(store, {
    elementIndex,
    slideIndex,
    elementId,
    slideId,
  });
};

// Device-aware helpers for preview (do not touch button styles below)
const isMobileDeviceMode = computed(() => store.deviceMode === "mobile");
const elementObj = computed(() => props.element || ({} as any));

const activePadding = computed<any>(() =>
  isMobileDeviceMode.value
    ? elementObj.value.mobilePadding || elementObj.value.padding || {}
    : elementObj.value.padding || {}
);
const activeBorder = computed<any>(() =>
  isMobileDeviceMode.value
    ? elementObj.value.mobileBorder || elementObj.value.border || {}
    : elementObj.value.border || {}
);
const activeShadow = computed<any>(() =>
  isMobileDeviceMode.value
    ? elementObj.value.mobileShadow || elementObj.value.shadow || {}
    : elementObj.value.shadow || {}
);
const activeBgColor = computed<string>(() => {
  const base = isMobileDeviceMode.value
    ? elementObj.value.mobileBgColor ?? elementObj.value.bgColor
    : elementObj.value.bgColor;
  const safe = String(base ?? "").replace(/^#/, "");
  return safe && safe.length > 0 ? safe : "FFFFFF";
});
const activeWeight = computed<number>(() => {
  const base = isMobileDeviceMode.value
    ? elementObj.value.mobileWeight ?? elementObj.value.weight
    : elementObj.value.weight;
  return Number(base ?? 400);
});
const activeTextColor = computed<string>(() =>
  isMobileDeviceMode.value
    ? props.fieldStyle?.mobileLabelColor ??
      props.fieldStyle?.color ??
      TERMS_AND_CONDITIONS_LABEL.DEFAULT_COLOR
    : props.fieldStyle?.color ?? TERMS_AND_CONDITIONS_LABEL.DEFAULT_COLOR
);

const applyTncColorForDisplay = (
  value: string,
  which?: "placeholder" | "placeholder2"
): string => {
  const tag = (props.element as any)?.tag;
  const input = value ? String(value).trim() : "";
  if (input.length > 0 && tag === "terms_and_conditions") {
    value = String(value).replace(
      /(<p\b[^>]*style=['"][^'"]*color:\s*)#[0-9a-fA-F]{3,8}/gi,
      `$1${activeTextColor.value}`
    );
    if (which && value !== input) {
      nextTick(() =>
        which === "placeholder"
          ? setTncPlaceholder(value)
          : setTncPlaceholder2(value)
      );
    }
  }
  return value;
};

// TNC device-aware placeholders
const activeTncPlaceholder = computed<string>(() => {
  const el = elementObj.value;
  const base = isMobileDeviceMode.value
    ? el?.mobilePlaceholder ?? el?.placeholder
    : el?.placeholder;
  return applyTncColorForDisplay(base ?? "", "placeholder");
});

const setTncPlaceholder = (value: string) => {
  if (isMobileDeviceMode.value) {
    (props.element as any).mobilePlaceholder = value;
  } else {
    (props.element as any).placeholder = value;
  }
  store.addToHistory();
};

const activeTncPlaceholder2 = computed<string>(() => {
  const el = elementObj.value;
  const base = isMobileDeviceMode.value
    ? el?.mobilePlaceholder2 ?? el?.placeholder2
    : el?.placeholder2;
  return applyTncColorForDisplay(base ?? "", "placeholder2");
});

const hasTncPlaceholder2 = computed<boolean>(() => {
  const el = elementObj.value;
  const raw = isMobileDeviceMode.value
    ? el?.mobilePlaceholder2 ?? el?.placeholder2
    : el?.placeholder2;
  return (raw ?? "").trim().length > 0;
});

const sourceAddedLabel = computed<string>(() => {
  if (store.quizId) return t("common.sourceAddedQuiz");
  if (store.surveyId) return t("common.sourceAddedSurvey");
  return t("common.sourceAddedForm");
});

const setTncPlaceholder2 = (value: string) => {
  if (isMobileDeviceMode.value) {
    (props.element as any).mobilePlaceholder2 = value;
  } else {
    (props.element as any).placeholder2 = value;
  }
  store.addToHistory();
};

const headingStyleCSS = computed(() => {
  const tag = (props.element as any)?.tag;
  const isRteText = tag === "header" || tag === "terms_and_conditions";
  const resolvedFontSize = isRteText
    ? 14
    : typeof props.element?.fontSize === "number" &&
      !Number.isNaN(props.element?.fontSize)
    ? (props.element as any).fontSize
    : 14;

  return {
    color: props.element?.color
      ? "#" + String(props.element?.color)
      : activeTextColor.value,
    fontFamily: props.element?.fontFamily
      ? `'${props.element?.fontFamily}'`
      : "Roboto",
    // Align preview with editor default base font-size for RTE content
    fontSize: String(resolvedFontSize) + "px",
    fontWeight: String(activeWeight.value),
    backgroundColor: "#" + String(activeBgColor.value),
    boxShadow:
      String(activeShadow.value?.horizontal ?? 0) +
      "px " +
      String(activeShadow.value?.vertical ?? 0) +
      "px " +
      String(activeShadow.value?.blur ?? 0) +
      "px " +
      String(activeShadow.value?.spread ?? 0) +
      "px #" +
      String(activeShadow.value?.color || "000000"),
    padding:
      String(activePadding.value?.top ?? 0) +
      "px " +
      String(activePadding.value?.right ?? 0) +
      "px " +
      String(activePadding.value?.bottom ?? 0) +
      "px " +
      String(activePadding.value?.left ?? 0) +
      "px",
    border:
      String(activeBorder.value?.border ?? 0) +
      "px " +
      String(activeBorder.value?.type || "solid") +
      " #" +
      String(activeBorder.value?.color || "000000"),
    borderRadius: String(activeBorder.value?.radius ?? 0) + "px",
    textAlign: "left",
    wordWrap: "break-word",
  } as Record<string, string>;
});
const tncTextStyleCSS = computed<Record<string, string>>(() => {
  const { color, fontFamily, fontSize, fontWeight, textAlign, wordWrap } =
    headingStyleCSS.value;
  return { color, fontFamily, fontSize, fontWeight, textAlign, wordWrap };
});
const buttonStyleCSS = computed(() => {
  // Use primary color if mapPrimaryColorToButtonColor is enabled and primaryColor exists
  const backgroundColor =
    store.fieldStyle?.mapPrimaryColorToButtonColor &&
    store.fieldStyle?.primaryColor
      ? "#" + store.fieldStyle.primaryColor
      : "#" + props.element.bgColor;

  const layout = currentLayout.value;

  const width =
    props.element?.fullwidth || store.deviceMode === "mobile"
      ? "100%"
      : layout === CONSTANTS.SINGLE_LINE && props.element?.fieldWidthPercentage
      ? "100%"
      : props.element?.fieldWidthPercentage
      ? props.element.fieldWidthPercentage + "%"
      : "50%";

  const marginTop = layout !== CONSTANTS.SINGLE_LINE ? "15px" : "0px";

  return {
    backgroundColor,
    padding:
      props.element?.padding?.top +
      "px " +
      props.element?.padding?.right +
      "px " +
      props.element?.padding?.bottom +
      "px " +
      props.element?.padding?.left +
      "px",
    border:
      props.element?.border +
      "px " +
      props.element?.borderType +
      " #" +
      props.element?.borderColor,
    borderRadius: (props.element.radius || 0) + "px",
    boxShadow:
      props.element?.shadow?.horizontal +
      "px " +
      props.element?.shadow?.vertical +
      "px " +
      props.element?.shadow?.blur +
      "px " +
      props.element?.shadow?.spread +
      "px #" +
      props.element?.shadow?.color,
    marginTop,
    width,
    whiteSpace: "normal" as const,
  };
});
const buttonTextAlignment = computed(() => {
  const defaultAlign = "center";

  if (typeof document === "undefined") {
    return defaultAlign;
  }

  const container = document.createElement("div");
  container.innerHTML = props.element?.label || "";

  const alignedNode = container.querySelector<HTMLElement>(
    "[style*='text-align']"
  );
  const alignValue = alignedNode?.style?.textAlign?.toLowerCase();

  if (
    alignValue &&
    ["left", "center", "right", "justify"].includes(alignValue)
  ) {
    return alignValue === "justify" ? "left" : alignValue;
  }

  return defaultAlign;
});
const buttonStyleTextCSS = computed(() => {
  const fontSizeValue = Number(props.element?.fontSize);
  return {
    color: "#" + props.element?.color,
    fontSize: Number.isFinite(fontSizeValue) ? `${fontSizeValue}px` : "inherit",
    fontWeight: props.element?.weight,
    fontFamily: props.element?.fontFamily
      ? `'${props.element?.fontFamily}'`
      : "inherit",
    lineHeight: "1.8",
    textAlign: buttonTextAlignment.value,
  };
});
const buttonSubTextStyleTextCSS = computed(() => {
  const subTextAlign = props.element?.align || "center";
  const fontSizeValue = Number(props.element?.subTextFontSize);

  return {
    color: "#" + props.element?.subTextColor,
    fontSize: Number.isFinite(fontSizeValue) ? `${fontSizeValue}px` : "inherit",
    fontWeight: props.element?.subTextWeight,
    fontFamily: props.element?.subTextFontFamily
      ? `'${props.element?.subTextFontFamily}'`
      : "inherit",
    lineHeight: "1.8",
    textAlign: subTextAlign,
  };
});
const getInputCSSList = (): string[] => {
  return store.builderConfig.inputStyleType === "line"
    ? ["line-form-style"]
    : [];
};

const labelAlignment = () => {
  const layout =
    store.deviceMode === "mobile"
      ? store.builderConfig.mobileLayout || store.builderConfig.layout
      : store.builderConfig.layout;

  if (layout === CONSTANTS.ONE_COLUMN && store.deviceMode !== "mobile") {
    const elementAlignment =
      !props.element.labelAlignment ||
      props.element.labelAlignment === "default"
        ? store.fieldStyle.labelAlignment
        : props.element.labelAlignment;
    const validAlign = ["left", "right"].includes(elementAlignment);
    return {
      width: validAlign ? store.fieldStyle.labelWidth + "px" : "",
      display: validAlign ? "inline-block" : "block",
      cssFloat: validAlign ? elementAlignment : "none",
      textAlign: validAlign ? elementAlignment : "",
      padding: validAlign ? "0 10px 0 0" : "",
    } as Record<string, string>;
  }
  return {} as Record<string, string>;
};
const getStyle = () => {
  return {
    fontSize: store.fieldStyle.placeholderFontSize + "px",
    width: "100%",
  } as Record<string, string>;
};
const getEmailPhoneValidateClass = (fieldWidthPercentage: number) => {
  return [CONSTANTS.TWO_COLUMN, CONSTANTS.SINGLE_LINE].includes(
    currentLayout.value
  ) && fieldWidthPercentage <= 50
    ? "block space-y-3"
    : "flex";
};

const currentLayout = computed(() => {
  return store.deviceMode === "mobile"
    ? store.builderConfig.mobileLayout || store.builderConfig.layout
    : store.builderConfig.layout;
});

const handleInputClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (!inputClicked.value) {
    // Ensure a text node exists so the caret can render even when empty
    let textNode = target.firstChild;
    if (!textNode || textNode.nodeType !== Node.TEXT_NODE) {
      const node = document.createTextNode(target.textContent || "");
      target.textContent = "";
      target.appendChild(node);
      textNode = node;
    }

    // Select all text within the contenteditable on first click
    const range = document.createRange();
    range.setStart(textNode, 0);
    range.setEnd(textNode, (textNode.textContent || "").length);
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
    inputClicked.value = true;
  }
  // For subsequent clicks, allow the browser to position the caret
};

// Function to check if HTML content has actual text
const hasTextContent = (html: string | undefined): boolean => {
  if (!html) {
    return false;
  }

  // Create a temporary div to parse the HTML
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  // Get the text content and remove whitespace
  const textContent = tempDiv.textContent || tempDiv.innerText || "";
  return textContent.trim().length > 0;
};

const shortLabelElement = ref<HTMLElement | null>(null);

// Auto-focus and select label text when this element becomes active
const isActive = computed(() =>
  isBuilderElementActive(store, {
    elementIndex: props.index,
    slideIndex: props.slideIndex,
    elementId: (props.element as any)?.uuid,
    slideId: (props.slide as any)?.id,
  })
);

watch(
  () => isActive.value,
  async (active) => {
    if (active) {
      await nextTick();
      focusLabelAfterActiveChange();
      // Prevent subsequent first-click from reselecting all text
      inputClicked.value = true;
    }
  }
);

const iconStyle = computed(() => {
  const base = Number(props.element?.fontSize) || 14;
  const size = Math.max(1, base + 2);
  const weight = props.element?.weight;
  return {
    fontSize: size + "px",
    lineHeight: "1",
    color: "#" + (props.element?.color || "000000"),
    ...(weight ? { fontWeight: String(weight) } : {}),
  } as Record<string, string>;
});

const resolvedIconPlacement = computed(() => {
  return props.element?.iconPlacement === "left" ? "left" : "right";
});

const rawInputLabelHandler = (e: Event) => {
  const target = e.target as HTMLElement;
  const caret = getCaretOffsetWithin(target);
  const text = (target.textContent || "").replace(/\u200B/g, "");
  localLabel.value = text;
  onLabelInput();
  handleSafariCursor(e);
  nextTick(() => {
    setCaretOffsetWithin(target, caret);
  });
};

const rawInputShortLabelHandler = (e: Event) => {
  handleShortLabelInputUtil(e, localShortLabel, updateLocalShortLabel);
};

const compositionEndHandler = handleCompositionEnd(rawInputLabelHandler);
const handleInputLabel = withIMECheck(rawInputLabelHandler);

const compositionEndHandlerShortLabel = handleCompositionEnd(
  rawInputShortLabelHandler
);
const handleInputShortLabel = withIMECheck(rawInputShortLabelHandler);
const buttonContentClasses = computed(() => {
  const alignment = buttonTextAlignment.value;
  const classes = ["button-content"];

  if (alignment === "left") {
    classes.push("button-content--left");
  } else if (alignment === "right") {
    classes.push("button-content--right");
  } else {
    classes.push("button-content--center");
  }

  return classes;
});
const buttonTextWrapperClasses = computed(() => {
  return {
    "button-text": true,
    "button-text--full": !props.element?.enableIcon,
  };
});
</script>

<template>
  <div class="menu-item">
    <div
      class="form-builder--item field-draggable-area"
      :data-active="
        isActiveElement(slideIndex, index, element.uuid, slide.id)
          ? 'true'
          : 'false'
      "
    >
      <div
        v-if="element.description !== undefined"
        style="display: block; overflow-wrap: break-word"
        v-html="element.description"
      ></div>

      <div
        style="display: inline-flex"
        :style="labelAlignment()"
        class="label-wrapper"
      >
        <div
          v-show="showFieldLabel(formLabelVisible, element)"
          class="label-container"
        >
          <div
            ref="editableLabel"
            :key="resetKey"
            class="label-input font-inter cursor-text border-0 bg-transparent p-0 text-sm shadow-none focus:border-gray-300 focus:ring-0"
            :data-field-type="element.type || element.tag"
            :data-placeholder="t('common.labelPlaceholder')"
            contenteditable="true"
            dir="ltr"
            style="direction: ltr; unicode-bidi: plaintext; text-align: left"
            @compositionstart="handleCompositionStart"
            @compositionend="compositionEndHandler"
            @blur="onLabelBlur"
            @input="handleInputLabel"
            @keydown="(e) => { if (!handleLabelTabNavigation(e as KeyboardEvent)) handleSafariCursor(e); }"
            @click="handleInputClick"
            v-text="localLabel"
          ></div>
          <span v-if="element.required" class="required-asterisk">*</span>
        </div>
        <div v-if="element.hidden">
          <small class="ml-[5px] rounded-sm bg-[#e1e1e1] px-[5px] py-[2px]">
            {{ t("common.hidden") }}
          </small>
        </div>
      </div>

      <TextElement
        v-if="
          (element.type == 'text' && element.tag !== 'phone') ||
          element.type == 'date'
        "
        :placeholder="element.placeholder"
        :class-list="[getInputCSSList()]"
        :style-obj="getStyle()"
        :element="element"
        :is-active="isActiveElement(slideIndex, index, element.uuid, slide.id)"
        @update:placeholder="onUpdatePlaceholder"
        @update:short-label="onUpdateShortLabel"
      ></TextElement>
      <div v-else-if="element.tag === 'email'" class="flex flex-col">
        <div
          class="relative gap-x-3 self-stretch"
          :class="[getEmailPhoneValidateClass(element.fieldWidthPercentage)]"
          :style="getStyle()"
        >
          <div class="relative flex flex-1">
            <Mail01Icon
              class="input-icon pointer-events-none absolute left-[12px] top-1/2 z-[1] h-[16px] !w-[16px] -translate-y-1/2"
            />
            <input
              v-model="localPlaceholder"
              :placeholder="localPlaceholder"
              class="form-placeholder form-control shadow-none focus:outline-transparent focus:ring-0"
              :class="getInputCSSList()"
              :style="{
                fontSize: store.fieldStyle.placeholderFontSize + 'px',
                paddingLeft: '36px',
              }"
              @input="updateLocalPlaceholder"
            />
          </div>
          <UIButton
            v-if="element?.enableValidateEmail && !element.hidden"
            id="verify-email"
            class="pointer-events-none h-auto"
            :style="{ fontSize: store.fieldStyle.placeholderFontSize + 'px' }"
            >{{ t("common.verifyEmail") }}</UIButton
          >
        </div>

        <template
          v-if="isActiveElement(slideIndex, index, element.uuid, slide.id)"
        >
          <div
            ref="shortLabelElement"
            :key="resetKey"
            contenteditable="true"
            class="short-label font-inter cursor-text border-0 bg-transparent p-0 text-sm shadow-none focus:outline-none"
            :data-placeholder="t('common.shortLabelPlaceholder')"
            @blur="updateLocalShortLabel"
            @input="handleInputShortLabel"
            @compositionstart="handleCompositionStart"
            @compositionend="compositionEndHandlerShortLabel"
            @keydown="handleSafariCursor"
            @click="(e) => e.stopPropagation()"
            @focus="(e) => e.stopPropagation()"
            v-text="localShortLabel"
          ></div>
        </template>
        <template v-else>
          <span
            v-if="element.shortLabel"
            class="short-label font-inter break-words text-sm"
          >
            {{ element.shortLabel }}
          </span>
        </template>
      </div>
      <div v-else-if="element.tag === 'phone'" class="flex flex-col">
        <div
          class="gap-x-3"
          :class="[getEmailPhoneValidateClass(element.fieldWidthPercentage)]"
          :style="getStyle()"
        >
          <input
            v-model="localPlaceholder"
            :placeholder="localPlaceholder"
            class="form-placeholder form-control shadow-none focus:outline-transparent focus:ring-0"
            :class="getInputCSSList()"
            :style="{ fontSize: store.fieldStyle.placeholderFontSize + 'px' }"
            @input="updateLocalPlaceholder"
          />
          <UIButton
            v-if="element?.enableValidatePhone && !element.hidden"
            id="verify-phone"
            class="pointer-events-none h-auto"
            :style="{ fontSize: store.fieldStyle.placeholderFontSize + 'px' }"
            >{{ t("common.verifyPhone") }}</UIButton
          >
        </div>
        <template
          v-if="isActiveElement(slideIndex, index, element.uuid, slide.id)"
        >
          <div
            ref="shortLabelElement"
            :key="resetKey"
            contenteditable="true"
            class="short-label font-inter cursor-text border-0 bg-transparent p-0 text-sm shadow-none focus:outline-none"
            :data-placeholder="t('common.shortLabelPlaceholder')"
            @blur="updateLocalShortLabel"
            @input="handleInputShortLabel"
            @compositionstart="handleCompositionStart"
            @compositionend="compositionEndHandlerShortLabel"
            @keydown="handleSafariCursor"
            @click="(e) => e.stopPropagation()"
            @focus="(e) => e.stopPropagation()"
            v-text="localShortLabel"
          ></div>
        </template>
        <template v-else>
          <span
            v-if="element.shortLabel"
            class="short-label font-inter break-words text-sm"
          >
            {{ element.shortLabel }}
          </span>
        </template>
      </div>
      <div v-else-if="element.tag === 'country'" class="flex flex-col">
        <SelectField
          :element="element"
          :placeholder="element.placeholder"
          :class="[getInputCSSList()]"
          :style="getStyle()"
          @update:placeholder="onUpdatePlaceholder"
        />
        <template
          v-if="isActiveElement(slideIndex, index, element.uuid, slide.id)"
        >
          <div
            ref="shortLabelElement"
            :key="resetKey"
            contenteditable="true"
            class="short-label font-inter cursor-text border-0 bg-transparent p-0 text-sm shadow-none focus:outline-none"
            :data-placeholder="t('common.shortLabelPlaceholder')"
            @blur="updateLocalShortLabel"
            @input="handleInputShortLabel"
            @compositionstart="handleCompositionStart"
            @compositionend="compositionEndHandlerShortLabel"
            @keydown="handleSafariCursor"
            @click="(e) => e.stopPropagation()"
            @focus="(e) => e.stopPropagation()"
            v-text="localShortLabel"
          ></div>
        </template>
        <template v-else>
          <span
            v-if="element.shortLabel"
            class="short-label font-inter break-words text-sm"
          >
            {{ element.shortLabel }}
          </span>
        </template>
      </div>
      <div v-else-if="element.tag === 'header'">
        <div class="text-element">
          <RichTextEditor
            v-if="isActiveElement(slideIndex, index, element.uuid, slide.id)"
            :model-value="activeLabel"
            fallback-font="inter"
            :fallback-font-size="16"
            size="mid"
            editor-height="auto"
            :mobile-inline="store.deviceMode === 'mobile'"
            :content-style="headingStyleCSS"
            :custom-width="store.deviceMode === 'mobile' ? '80%' : '66rem'"
            @update:model-value="
              (value) => {
                localLabel = value;
                onLabelInput();
              }
            "
          />
          <div
            v-else
            class="break-words"
            :style="headingStyleCSS"
            v-html="activeLabel"
          ></div>
        </div>
      </div>
      <div v-else-if="element.tag === 'html'">
        <h5 v-if="element.html == ''">
          {{ element.placeholder }}
        </h5>
        <div v-else>
          <div>
            <small
              style="background: #e1e1e1; padding: 2px 5px; border-radius: 2px"
              >{{ t("common.scriptAdded") }}</small
            >
          </div>
          <div v-html="element.html"></div>
        </div>
      </div>

      <StandardImageUpload
        v-else-if="element.tag === 'image'"
        v-model:value="element.url"
        :element="element"
        :index="index"
        :media-center-sub-category="props.mediaCenterSubCategory"
      />

      <div v-else-if="element.tag === 'captcha'">
        <CaptchaIcon class="captcha-wrap-img" />
      </div>
      <div v-else-if="element.tag === 'source'">
        <div>
          <small
            class="rounded-[4px] bg-[#f2f4f7] px-[8px] py-[4px] font-sans text-[16px] font-[500] leading-[24px] tracking-normal text-[#344054]"
          >
            {{ sourceAddedLabel }}
          </small>
        </div>
        <div>
          {{ element.value }}
        </div>
      </div>
      <div
        v-else-if="element.tag === 'button'"
        :style="{ textAlign: element.align }"
      >
        <button
          id="submit-button"
          type="button"
          :label="element.label"
          class="btn btn-dark button-element"
          :style="buttonStyleCSS"
        >
          <span :class="buttonContentClasses">
            <span
              v-if="element?.enableIcon && resolvedIconPlacement === 'left'"
              class="material-symbols-outlined"
              :style="iconStyle"
              aria-hidden="true"
            >
              {{ element?.iconType || "arrow_forward" }}
            </span>
            <span :class="buttonTextWrapperClasses">
              <InlineRTE
                v-model:model-value="element.label"
                :style-obj="buttonStyleTextCSS"
                :is-button="true"
                :is-active="
                  isActiveElement(slideIndex, index, element.uuid, slide.id)
                "
              />
            </span>
            <span
              v-if="element?.enableIcon && resolvedIconPlacement === 'right'"
              class="material-symbols-outlined"
              :style="iconStyle"
              aria-hidden="true"
            >
              {{ element?.iconType || "arrow_forward" }}
            </span>
          </span>
          <InlineRTE
            v-if="
              element?.submitSubText && hasTextContent(element.submitSubText)
            "
            v-model:model-value="element.submitSubText"
            :style-obj="buttonSubTextStyleTextCSS"
            :is-button="true"
            :is-active="
              isActiveElement(slideIndex, index, element.uuid, slide.id)
            "
          />
        </button>
      </div>
      <div v-else-if="element.tag === 'terms_and_conditions'">
        <div id="tnc-element" class="flex flex-col">
          <div class="flex items-baseline">
            <input
              id="terms-and-conditions"
              class="mr-1 !h-[16px] !w-[16px]"
              type="checkbox"
            />
            <div class="ml-2 min-w-0 flex-1">
              <RichTextEditor
                v-if="
                  isActiveElement(slideIndex, index, element.uuid, slide.id)
                "
                :model-value="activeTncPlaceholder"
                size="mid"
                editor-height="auto"
                :mobile-inline="store.deviceMode === 'mobile'"
                :content-style="headingStyleCSS"
                :custom-width="store.deviceMode === 'mobile' ? '72%' : '66rem'"
                fallback-font="inter"
                :fallback-font-size="16"
                @update:model-value="
                  (value) => {
                    setTncPlaceholder(value);
                  }
                "
              />
              <div
                v-else
                class="tnc"
                :style="tncTextStyleCSS"
                v-html="activeTncPlaceholder"
              />
            </div>
          </div>
          <div class="mt-2 flex w-full items-baseline">
            <input
              v-if="hasTncPlaceholder2"
              id="terms-and-conditions-2"
              class="mr-1 !h-[16px] !w-[16px]"
              type="checkbox"
            />
            <div v-if="hasTncPlaceholder2" class="ml-2 min-w-0 flex-1">
              <RichTextEditor
                v-if="
                  isActiveElement(slideIndex, index, element.uuid, slide.id)
                "
                :model-value="activeTncPlaceholder2"
                size="mid"
                editor-height="auto"
                :mobile-inline="store.deviceMode === 'mobile'"
                :content-style="headingStyleCSS"
                :custom-width="store.deviceMode === 'mobile' ? '72%' : '66rem'"
                fallback-font="inter"
                :fallback-font-size="16"
                @update:model-value="
                  (value) => {
                    setTncPlaceholder2(value);
                  }
                "
              />
              <div
                v-else
                class="tnc flex-1"
                :style="tncTextStyleCSS"
                v-html="activeTncPlaceholder2"
              />
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="element.tag === 'payment'">
        <Payment
          :payment="element"
          :input-class="getInputCSSList()"
          @open-settings="openSettings(index, slideIndex)"
        ></Payment>
      </div>
      <div v-else-if="element.tag === 'group_address'">
        <AddressCardPreview
          :grouped-element="store.addressSettings"
          :form-label-visible="formLabelVisible"
          :selected-slide-index="slideIndex"
          :selected-index="index"
        />
      </div>
      <div v-else class="builder-item">
        {{ element }}
      </div>
    </div>
    <div
      v-if="isActiveElement(slideIndex, index, element.uuid, slide.id)"
      class="outline-border"
    ></div>
    <div
      v-if="isActiveElement(slideIndex, index, element.uuid, slide.id)"
      class="builder-actions"
    >
      <a
        class="builder-action"
        :aria-label="t('common.openSettings')"
        @mousedown.stop="openSettings(index, slideIndex)"
      >
        <Settings02Icon class="h-4 w-4"></Settings02Icon>
      </a>
      <a
        v-if="
          store.duplicateAllowed && store.duplicateAllowed.includes(element.tag)
        "
        class="builder-action"
        :aria-label="t('common.cloneField')"
        @mousedown.stop="cloneElement(index, slideIndex)"
      >
        <Copy05Icon class="h-4 w-4"></Copy05Icon>
      </a>
      <a
        class="builder-action"
        :aria-label="t('common.removeField')"
        @mousedown.stop="deleteActiveElement(index, slideIndex)"
      >
        <Trash01Icon class="h-4 w-4"></Trash01Icon>
      </a>
    </div>
    <div
      v-if="
        isActiveElement(slideIndex, index, element.uuid, slide.id) &&
        !store.leftSidebarOpen
      "
      class="add-new-element"
      @mousedown.stop="() => emitter.emit('open-left-side-bar')"
    >
      <PlusIcon class="h-4 w-4"></PlusIcon>
    </div>
  </div>
  <AlertModal
    v-if="showDeleteModal"
    :description="t('form.conditonalLogic.deleteAlertMessage')"
    :title="t('form.conditonalLogic.deleteField')"
    :positive-text="t('common.delete')"
    :negative-text="t('common.cancel')"
    :show-modal-popup="showDeleteModal"
    header-type="error"
    footer-type="error"
    @pop:change="cancelCallbackDeleteUpdate"
    @pop:positive="successCallbackDeleteUpdate"
    @pop:negative="cancelCallbackDeleteUpdate"
  />
</template>

<style>
.close-icon {
  position: absolute;
  right: -10px;
  top: -10px;
  opacity: 1;
  -webkit-transition: all 0.5s ease-in;
  transition: all 0.5s ease-in;
  font-size: 10px;
  /* padding: 5px 7px; */
  background: #f5f5f5;
  color: #2a3135;
  border-radius: 50%;
  height: 20px;
  cursor: pointer;
}
.menu-item {
  position: relative;
}

.form-builder--item {
  border-radius: 4px;
  border: 1px solid transparent;
  -webkit-transition: border 0.2s ease-in-out;
  -o-transition: border 0.2s ease-in-out;
  transition: border 0.2s ease-in-out;
  position: relative;
}

.form-builder--item h1,
.form-builder--item h2,
.form-builder--item h3,
.form-builder--item h4,
.form-builder--item h5,
.form-builder--item h6 {
  margin-bottom: 0;
}
.form-builder--item label {
  font-size: 80%;
  color: #2a3135;
  overflow-wrap: anywhere;
  hyphens: auto;
}
.form-builder--item label span {
  padding-left: 5px;
}

.captcha-wrap-img {
  /* width: 304px; */
  display: block;
}
.button-element {
  min-width: 85px;
}
.button-content {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}
.button-content--left {
  justify-content: flex-start;
  text-align: left;
}
.button-content--center {
  justify-content: center;
  text-align: center;
}
.button-content--right {
  justify-content: flex-end;
  text-align: right;
}
.button-text {
  display: flex;
  min-width: 0;
}
.button-text--full {
  flex: 1 1 100%;
  width: 100%;
}
.button-text > * {
  width: 100%;
}
.button-text :deep(#inlineRichTextEditor),
.button-text :deep(#inlineRichTextEditor .ProseMirror) {
  width: 100%;
}

#submit-button blockquote,
#submit-button dd,
#submit-button dl,
#submit-button figure,
#submit-button h1,
#submit-button h2,
#submit-button h3,
#submit-button h4,
#submit-button h5,
#submit-button h6,
#submit-button hr,
#submit-button p,
#submit-button pre {
  margin: 0 !important;
}

#tnc-element {
  word-break: break-word;
}

.label-container {
  display: inline;
  width: auto;
  overflow: visible;
  margin-bottom: 6px;
  position: relative;
  max-width: 100%;
}

.label-input {
  display: inline;
  min-width: 0;
  max-width: 100%;
  outline: none;
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  margin-bottom: 2px;
  word-break: break-word;
  min-height: 24px;
  position: relative; /* allow fake caret positioning */
}

/* Special styling for date and dropdown fields */
.label-input[data-field-type="DATE"],
.label-input[data-field-type="SINGLE_OPTIONS"],
.label-input[data-field-type="MULTIPLE_OPTIONS"] {
  min-width: 100px;
  max-width: none;
}

.label-input:focus {
  background-color: rgba(200, 200, 200, 0.1);
  border-radius: 2px;
  outline: none;
}

.required-asterisk {
  font-weight: bold;
  padding-left: 0;
  font-size: 14px;
  display: inline;
  margin-left: 4px;
}

.label-wrapper {
  display: flex;
}

.short-label:empty:before {
  content: attr(data-placeholder);
}

[contenteditable="true"] {
  direction: ltr !important;
  unicode-bidi: plaintext !important;
  text-align: left !important;
  -webkit-rtl-ordering: logical !important;
}

/* Only show placeholder when element is active */
.label-input:empty:before {
  content: "";
  pointer-events: none; /* ensure placeholder never blocks caret */
}

/* Show placeholder when inside an active element */
*[data-active="true"] .label-input:empty:before,
.form-builder--item:has(.ProseMirror:focus) .label-input:empty:before,
.label-input:focus:empty:before {
  content: attr(data-placeholder);
  color: #9ca3af;
  font-style: italic;
  pointer-events: none; /* allow clicks to focus contenteditable */
  margin-left: 2px; /* leave room for fake caret */
}

/* Fake caret when focused and empty so caret + placeholder appear together */
.label-input:focus:empty::after {
  content: "";
  position: absolute;
  left: 0;
  top: 0.15em;
  width: 1px;
  height: 1em;
  background: currentColor;
  pointer-events: none;
  animation: blink-caret 1s steps(1, end) infinite;
}

@keyframes blink-caret {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}

.builder-actions {
  position: absolute;
  right: -8px;
  top: -8px;
  display: flex;
  gap: 0;
  background: #155eef;
  border-radius: 0 8px 0 8px;
  padding: 0 8px;
  padding-bottom: 2px;
  z-index: 1;
}
.builder-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 25px;
  height: 25px;
  color: #ffffff;
  cursor: pointer;
}
.builder-action:hover svg {
  transform: scale(1.15);
  transition: transform 0.25s ease;
}

.menu-item {
  position: relative;
}

.outline-border {
  position: absolute;
  top: -8px;
  left: -8px;
  right: -8px;
  bottom: -8px;
  border: 2px solid #155eef;
  border-radius: 8px;
  pointer-events: none;
  z-index: 1;
}

.add-new-element {
  position: absolute;
  left: 50%;
  bottom: -18px;
  transform: translateX(-50%);
  width: 24px;
  height: 24px;
  background-color: #155eef;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  cursor: pointer;
  z-index: 2;
}

/* Extend active background to outline border to avoid white gap */
.menu-item.active::before,
.form-builder--item[data-active="true"]::before {
  content: "";
  position: absolute;
  top: -8px;
  right: -8px;
  bottom: -8px;
  left: -8px;
  background: rgba(0, 78, 235, 0.05);
  border-radius: 8px;
  pointer-events: none;
}

input[type="checkbox"] {
  -webkit-appearance: none;
  appearance: none;
  width: 16px !important;
  height: 16px !important;
  border-radius: 4px !important;
}
</style>
